import React, { useMemo, useState } from 'react';
import { ArrowDown, ArrowUp, ArrowUpDown, Download, Search } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';

const fmtInt = (n) => (n === null || n === undefined ? '—' : Number(n).toLocaleString('en-ZA'));
const fmtRand = (n) => {
  if (n === null || n === undefined) return '—';
  if (n >= 1e6) return `R${(n / 1e6).toFixed(2)}m`;
  return `R${Number(n).toLocaleString('en-ZA')}`;
};
const fmtPct = (x) => (x === null || x === undefined ? '—' : `${(x * 100).toFixed(1)}%`);

// Visible columns. `rank` is the order the dashboard served (its own ranking),
// kept so a re-sorted table still shows where each area stood.
const COLUMNS = [
  { key: 'rank', label: '#', numeric: true, fmt: (v) => v },
  { key: 'area', label: 'Area' },
  { key: 'office', label: 'Office' },
  { key: 'region', label: 'Region' },
  { key: 'entity', label: 'Branch/Franchise' },
  { key: 'transfers', label: 'Transfers a year', numeric: true, fmt: fmtInt },
  { key: 'median_price', label: 'Median price', numeric: true, fmt: fmtRand },
  { key: 'our_share', label: 'Our share', numeric: true, fmt: fmtPct },
];

// CSV columns: everything a geo-targeting upload needs, map pin included.
const CSV_COLUMNS = ['rank', 'area', 'office', 'region', 'entity', 'transfers', 'median_price',
  'typical_value', 'total_value', 'our_share', 'lat', 'lng', 'radius_km'];

function csvCell(v) {
  if (v === null || v === undefined) return '';
  const s = String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export function toCsv(rows) {
  const cols = CSV_COLUMNS.filter((c) => rows.some((r) => r[c] !== undefined));
  return [cols.join(','), ...rows.map((r) => cols.map((c) => csvCell(r[c])).join(','))].join('\n');
}

function compare(a, b, key) {
  const x = a[key];
  const y = b[key];
  if (x === null || x === undefined) return 1;      // blanks last, either direction
  if (y === null || y === undefined) return -1;
  if (typeof x === 'number' && typeof y === 'number') return x - y;
  return String(x).localeCompare(String(y), 'en-ZA', { sensitivity: 'base' });
}

/**
 * The high-value areas list, filterable by area name, region, office and
 * side (Branch / Franchise, the row's `entity`) and
 * sortable by any column. Everything happens on the rows already loaded: the
 * list is area-level figures with no personal data, so the CSV is built here,
 * from exactly the rows on screen.
 */
export default function AreasTable({ areas, buildId }) {
  const ranked = useMemo(
    () => (areas || []).map((a, i) => ({ ...a, rank: i + 1 })),
    [areas],
  );
  const [query, setQuery] = useState('');
  const [region, setRegion] = useState('all');
  const [office, setOffice] = useState('all');
  const [entity, setEntity] = useState('all');
  const [sort, setSort] = useState({ key: 'rank', dir: 'asc' });

  const regions = useMemo(
    () => [...new Set(ranked.map((a) => a.region).filter(Boolean))].sort(),
    [ranked],
  );
  const entities = useMemo(
    () => [...new Set(ranked.map((a) => a.entity).filter(Boolean))].sort(),
    [ranked],
  );
  const offices = useMemo(
    () => [...new Set(ranked
      .filter((a) => (region === 'all' || a.region === region)
        && (entity === 'all' || a.entity === entity))
      .map((a) => a.office)
      .filter(Boolean))].sort(),
    [ranked, region, entity],
  );

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    const rows = ranked.filter((a) => (region === 'all' || a.region === region)
      && (office === 'all' || a.office === office)
      && (entity === 'all' || a.entity === entity)
      && (!q || String(a.area || '').toLowerCase().includes(q)));
    const sorted = [...rows].sort((a, b) => compare(a, b, sort.key));
    if (sort.dir === 'desc') {
      // keep blanks last when reversing
      const blanks = sorted.filter((r) => r[sort.key] === null || r[sort.key] === undefined);
      const vals = sorted.filter((r) => !(r[sort.key] === null || r[sort.key] === undefined));
      return [...vals.reverse(), ...blanks];
    }
    return sorted;
  }, [ranked, query, region, office, entity, sort]);

  const toggleSort = (key, numeric) => {
    setSort((s) => (s.key === key
      ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' }
      // numbers start biggest-first (what you scan for); text starts A-Z
      : { key, dir: numeric && key !== 'rank' ? 'desc' : 'asc' }));
  };

  const download = () => {
    const blob = new Blob([toCsv(shown)], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const scope = [region, office, entity].filter((v) => v !== 'all').join('_').replace(/[^A-Za-z0-9_-]+/g, '-');
    a.href = url;
    a.download = `high_value_areas${buildId ? `_${buildId}` : ''}${scope ? `_${scope}` : ''}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const filtered = query || region !== 'all' || office !== 'all' || entity !== 'all';

  return (
    <div className="space-y-3" data-testid="areas-table">
      <div className="flex flex-col gap-3 md:flex-row md:items-end">
        <div className="space-y-1 w-full md:w-64">
          <Label htmlFor="areas-search">Area</Label>
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              id="areas-search"
              data-testid="areas-search"
              placeholder="Search areas"
              className="pl-8"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
        </div>
        <div className="space-y-1 w-full md:w-56">
          <Label htmlFor="areas-region">Region</Label>
          <Select value={region} onValueChange={(v) => { setRegion(v); setOffice('all'); }}>
            <SelectTrigger id="areas-region" data-testid="areas-region">
              <SelectValue placeholder="All regions" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All regions</SelectItem>
              {regions.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1 w-full md:w-56">
          <Label htmlFor="areas-office">Office</Label>
          <Select value={office} onValueChange={setOffice}>
            <SelectTrigger id="areas-office" data-testid="areas-office">
              <SelectValue placeholder="All offices" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All offices</SelectItem>
              {offices.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1 w-full md:w-44">
          <Label htmlFor="areas-entity">Branch / Franchise</Label>
          <Select value={entity} onValueChange={(v) => { setEntity(v); setOffice('all'); }}>
            <SelectTrigger id="areas-entity" data-testid="areas-entity">
              <SelectValue placeholder="All" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              {entities.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-3 md:ml-auto">
          {filtered && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { setQuery(''); setRegion('all'); setOffice('all'); setEntity('all'); }}
            >
              Clear
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={download}
            disabled={!shown.length}
            data-testid="areas-download"
          >
            <Download className="h-4 w-4 mr-1" /> Download CSV
          </Button>
        </div>
      </div>

      <p className="text-sm text-muted-foreground" data-testid="areas-count">
        Showing {fmtInt(shown.length)} of {fmtInt(ranked.length)} areas
        {filtered ? ' (filtered); the CSV holds the rows shown' : ''}.
      </p>

      <div className="rounded-lg border overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              {COLUMNS.map((c) => {
                const active = sort.key === c.key;
                const Icon = !active ? ArrowUpDown : sort.dir === 'asc' ? ArrowUp : ArrowDown;
                return (
                  <TableHead
                    key={c.key}
                    className={c.numeric ? 'text-right' : undefined}
                    aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
                  >
                    <button
                      type="button"
                      onClick={() => toggleSort(c.key, c.numeric)}
                      className={`inline-flex items-center gap-1 hover:text-foreground ${active ? 'text-foreground font-semibold' : ''}`}
                      data-testid={`areas-sort-${c.key}`}
                    >
                      {c.label}
                      <Icon className="h-3.5 w-3.5" />
                    </button>
                  </TableHead>
                );
              })}
            </TableRow>
          </TableHeader>
          <TableBody>
            {shown.map((a) => (
              <TableRow key={`${a.rank}|${a.area}|${a.office}`}>
                {COLUMNS.map((c) => (
                  <TableCell
                    key={c.key}
                    className={c.numeric ? 'text-right tabular-nums' : (c.key === 'area' ? 'font-medium' : undefined)}
                  >
                    {c.fmt ? c.fmt(a[c.key]) : a[c.key]}
                  </TableCell>
                ))}
              </TableRow>
            ))}
            {!shown.length && (
              <TableRow>
                <TableCell colSpan={COLUMNS.length} className="text-center text-sm text-muted-foreground py-6">
                  No areas match these filters.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
